import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Button} from '@gravity-ui/uikit';
import {useConversationMessages, ConversationAudience} from '../../../entities/conversation/hooks/useConversationMessages';
import {useUsersByIds} from '../../../entities/users';
import {AuthorKind, Message, useConversationSeen} from '../../../entities/conversation';
import {useIsElementVisible} from '../../../shared/hooks/useIsElementVisible';
import {MessageBubble} from './MessageBubble';
import {MessageComposer} from './MessageComposer';
import styles from './ConversationFeed.module.scss';

interface ConversationFeedProps {
  appealId: string;
  audience: ConversationAudience;
}

// Подтверждение просмотра требует не мгновения видимости, а нескольких секунд
// присутствия — иначе мелькание карточки в списке засчиталось бы как прочтение.
const PRESENCE_DELAY_MS = 3000;

// Повторы самой мутации (`retry: 2`) закрывают одну сетевую осечку, но не
// отказ подольше. Когда попытки исчерпаны, условия присутствия не меняются —
// эффект новую отправку сам не запланирует, и отметка потеряется до
// следующего захода. Поэтому подтверждение перепланируется с нарастающей
// паузой, но конечное число раз: недоступный сервер не должен получать
// бесконечный поток попыток от открытой вкладки.
const MARK_RETRY_DELAYS_MS = [15000, 60000];

function collectUserIds(messages: Message[]): string[] {
  const ids = new Set<string>();
  for (const msg of messages) {
    if (msg.authorId) {
      ids.add(msg.authorId);
    }
  }
  return Array.from(ids);
}

function useIsTabVisible(): boolean {
  const [isVisible, setIsVisible] = useState(() => document.visibilityState === 'visible');

  useEffect(() => {
    const handleVisibilityChange = () => setIsVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  return isVisible;
}

export const ConversationFeed: React.FC<ConversationFeedProps> = ({appealId, audience}) => {
  const {data, isLoading, isError, hasNextPage, fetchNextPage, isFetchingNextPage} = useConversationMessages(appealId, audience);
  const {seenState, isSeenStateFresh, isSeenStateSettled, markSeen, isMarkError} = useConversationSeen(appealId, audience);

  const allMessages = useMemo(() => {
    if (!data) return [];
    const seen = new Set<string>();
    const result: Message[] = [];
    for (const page of [...data.pages].reverse()) {
      for (const msg of page.items) {
        if (!seen.has(msg.id)) {
          seen.add(msg.id);
          result.push(msg);
        }
      }
    }
    return result;
  }, [data]);

  const userIds = useMemo(() => collectUserIds(allMessages), [allMessages]);
  const {usersData} = useUsersByIds([...userIds].sort());

  // Узел нижнего края нужен и для scrollIntoView (bottomRef), и для наблюдателя
  // видимости (bottomNode в состоянии) — запись в ref.current не вызывает
  // ре-рендер, и появление якоря в DOM было бы не отследить.
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const [bottomNode, setBottomNode] = useState<HTMLDivElement | null>(null);
  const setBottom = useCallback((node: HTMLDivElement | null) => {
    bottomRef.current = node;
    setBottomNode(node);
  }, []);

  const prevLastIdRef = useRef<string | undefined>(undefined);

  const didInitialScrollRef = useRef(false);

  // Ключ открытой Переписки: сброс делается внутри самого эффекта прокрутки, а
  // не отдельным эффектом ниже, — эффекты выполняются в порядке объявления, и
  // отдельный сброс опоздал бы ровно на тот кадр, в котором пришли сообщения
  // новой Переписки.
  const scrolledConversationRef = useRef<string | null>(null);

  useEffect(() => {
    // Смена Заявки или Стороны — это другая лента. Точки монтирования компонент
    // не пересоздают (`key` не задан), поэтому без сброса первый показ новой
    // Переписки доезжал бы до низа плавной анимацией, как дописанная реплика, а
    // последним известным сообщением считалась бы реплика прошлого диалога.
    const conversationKey = `${audience}:${appealId}`;
    if (scrolledConversationRef.current !== conversationKey) {
      scrolledConversationRef.current = conversationKey;
      didInitialScrollRef.current = false;
      prevLastIdRef.current = undefined;
    }

    const lastId = allMessages.length ? allMessages[allMessages.length - 1].id : undefined;
    const appended = lastId !== undefined && lastId !== prevLastIdRef.current;
    if (appended && bottomRef.current?.scrollIntoView) {
      if (!didInitialScrollRef.current) {
        didInitialScrollRef.current = true;
        bottomRef.current.scrollIntoView();
      } else {
        bottomRef.current.scrollIntoView({behavior: 'smooth'});
      }
    }
    prevLastIdRef.current = lastId;
  }, [allMessages, appealId, audience]);

  // Присутствие: низ ленты виден, вкладка в фокусе, и есть что подтверждать.
  const isBottomVisible = useIsElementVisible(bottomNode);
  const isTabVisible = useIsTabVisible();
  const isPresent = isBottomVisible && isTabVisible;

  // Граница разделителя — снимок, зафиксированный один раз при первом ответе
  // состояния просмотра, а не живое значение (иначе разделитель исчез бы в
  // ту же секунду, когда сработает подтверждение просмотра).
  const [boundary, setBoundary] = useState<string | null | undefined>(undefined);

  // Смена Заявки или Стороны — это другая переписка: снимок снимается заново.
  // Точки монтирования компонент не пересоздают (`key` не задан), поэтому без
  // сброса разделитель встал бы по границе предыдущего диалога.
  useEffect(() => {
    setBoundary(undefined);
  }, [appealId, audience]);

  // Снимок снимается только с перечитанного состояния: кэш React Query отдаёт
  // синхронно, и по нему граница встала бы до ответа сервера — ровно тем
  // устаревшим значением, ради которого запрос и перезапрашивается. Пока
  // успешного ответа нет — в том числе когда запрос отказал и в руках остался
  // один кэш, — разделитель не рисуется вовсе: «Новые сообщения» над уже
  // прочитанным коллегой из другой сессии хуже, чем отсутствие разделителя.
  useEffect(() => {
    if (boundary === undefined && seenState !== undefined && isSeenStateFresh) {
      setBoundary(seenState.seenAt);
    }
  }, [boundary, seenState, isSeenStateFresh]);

  const lastMessage = allMessages[allMessages.length - 1] as Message | undefined;

  // На холодном кэше отказавшее чтение не оставляет состояния вовсе. Считать,
  // что подтверждать нечего, значило бы потерять отметку до следующего захода:
  // опроса у запроса нет, сам он не повторится, и открытая лента так и стояла
  // бы непрочитанной. Отправка вслепую при этом безопасна: сервер применяет
  // отметку монотонно, и подтверждение по последнему показанному сообщению
  // границу назад не сдвинет. Успешная отправка запишет состояние в кэш —
  // повторных подтверждений по той же цели не будет.
  const hasSomethingToMark =
    lastMessage !== undefined &&
    (seenState === undefined
      ? isSeenStateSettled
      : seenState.seenAt === null || lastMessage.createdAt > seenState.seenAt);

  // Подтверждать просмотр можно только после того, как снимок границы уже снят.
  // Иначе выдержка присутствия отсчитывалась бы по кэшу, и при небыстром
  // перечитывании подтверждение успело бы уйти на сервер раньше ответа: снимок
  // взялся бы уже с собственной отметки, и разделитель «Новые сообщения» не
  // показался бы вовсе. Отказавший запрос состояния подтверждение не запирает:
  // снимка не будет вовсе, гонки с ответом тоже, а присутствие человека от
  // доступности чтения отметки не зависит.
  const canMark = hasSomethingToMark && (boundary !== undefined || isSeenStateSettled);

  // Что именно предстоит подтвердить. Бюджет перепланирований завязан на саму
  // цель, а не на булев признак «есть что подтверждать»: при исчерпанных
  // попытках он остался бы `true` и пришедшее следом сообщение получило бы
  // только одну отправку без повторов.
  const markTarget = hasSomethingToMark ? lastMessage?.createdAt : undefined;

  // Счётчик перепланирований после отказа: смена Переписки и новая цель
  // подтверждения открывают бюджет попыток заново.
  const [markRetry, setMarkRetry] = useState(0);

  useEffect(() => {
    setMarkRetry(0);
  }, [appealId, audience, markTarget]);

  useEffect(() => {
    if (!isPresent || !canMark || lastMessage === undefined) {
      return;
    }
    const createdAt = lastMessage.createdAt;
    const timer = setTimeout(() => {
      markSeen(createdAt);
    }, PRESENCE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [isPresent, canMark, lastMessage, markSeen, markRetry]);

  useEffect(() => {
    if (!isMarkError || !isPresent || !canMark || markRetry >= MARK_RETRY_DELAYS_MS.length) {
      return;
    }
    const timer = setTimeout(() => setMarkRetry(markRetry + 1), MARK_RETRY_DELAYS_MS[markRetry]);
    return () => clearTimeout(timer);
  }, [isMarkError, isPresent, canMark, markRetry]);

  const ownKind: AuthorKind = audience === 'admin' ? 'Administrator' : 'Representative';

  const dividerBeforeId = useMemo(() => {
    if (boundary === undefined) return null;
    const first = allMessages.find(
      (m) => m.authorKind !== ownKind && (boundary === null || m.createdAt > boundary),
    );
    return first?.id ?? null;
  }, [allMessages, ownKind, boundary]);

  return (
    <aside className={styles.feed}>
      {isError ? (
        <div className={styles.feedEmpty}>Не удалось загрузить переписку. Попробуйте обновить страницу</div>
      ) : allMessages.length === 0 && !isLoading ? (
        <div className={styles.feedEmpty}>Пока нет сообщений. Напишите первым</div>
      ) : (
        <div className={styles.feedList}>
          {hasNextPage && (
            <div className={styles.feedLoadMore}>
              <Button view="flat" size="s" loading={isFetchingNextPage} onClick={() => fetchNextPage()}>
                Загрузить предыдущие
              </Button>
            </div>
          )}
          {allMessages.map((msg) => (
            <React.Fragment key={msg.id}>
              {msg.id === dividerBeforeId && (
                <div className={styles.feedDivider} role="separator" aria-label="Новые сообщения">
                  <span className={styles.feedDividerLabel}>Новые сообщения</span>
                </div>
              )}
              <MessageBubble message={msg} audience={audience} usersData={usersData} />
            </React.Fragment>
          ))}
          <div ref={setBottom} />
        </div>
      )}
      {!isError && <MessageComposer appealId={appealId} audience={audience} />}
    </aside>
  );
};
